import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { Switch } from '@snowball-tech/fractal/dist/src/components/Switch'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import {
  UilAngleLeftB as ChevronLeft,
  UilAngleRightB as ChevronRight,
  UilEdit as EditIcon,
} from '@tooni/iconscout-unicons-react'
import { type NumberSize, type ResizeDirection, Resizable } from 're-resizable'

import {
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
  type SyntheticEvent,
  useCallback,
  useState,
} from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNumber from 'lodash/fp/isNumber'
import omit from 'lodash/fp/omit'

import type { Progress } from '../../../api/endpoints'
import type { ImageSize } from '../../../types'

import { ObjectFits } from '../../../enums'
import useTranslations from '../../../hooks/useTranslations'
import { asPx, spacings } from '../../../styles/helpers'
import ImageDisplay from '../../Edition/ImageDisplay'
import AddImageButton from './AddImageButton'

type Props = {
  setIsImageLoading: Dispatch<SetStateAction<boolean>>
  allowFitChange?: boolean
  allowPaddingChange?: boolean
  buttonLabel?: string
  color?:
    | string
    | 'black'
    | 'blue'
    | 'green'
    | 'light-pink'
    | 'pink'
    | 'purple'
    | 'transparent'
    | 'white'
    | 'yellow'
  editable?: boolean
  elevation?: '0' | PaperElevations
  imageUrl?: string
  isImageLoading?: boolean
  maxWidth?: number | string
  objectFit?: ObjectFits
  progress?: Progress | null
  resizable?: boolean
  size?: ImageSize
  small?: boolean
  withHint?: boolean
  withLoader?: boolean
  onObjectFitChange?: (objectFit: ObjectFits) => void
  onPaddingChange?: (padding: 'none' | 'small') => void
  onSizeChange?: (size: ImageSize) => void
} & Omit<
  ComponentProps<typeof ImageDisplay>,
  'exitZoom' | 'onChange' | 'setIsLoading' | 'url' | 'zoomed'
>
export default function Trigger({
  allowFitChange = true,
  allowPaddingChange = false,
  buttonLabel,
  color,
  editable = true,
  elevation = PaperElevations.Elevated,
  imageUrl,
  isImageLoading = false,
  maxWidth = '100%',
  objectFit,
  onObjectFitChange,
  onPaddingChange,
  onSizeChange,
  padding = 'none',
  progress = null,
  resizable = true,
  setIsImageLoading,
  size,
  small = false,
  withHint = false,
  withLoader = true,
  ...imageProps
}: Props) {
  const $t = useTranslations()

  const actualColor: typeof color = isEmpty(color)
    ? elevation === '0'
      ? 'transparent'
      : 'white'
    : color

  const handleObjectFitChange = (isCoverFit: boolean) => {
    onObjectFitChange?.(isCoverFit ? ObjectFits.Cover : ObjectFits.Contain)
  }

  const handlePaddingChange = (isPadded: boolean) => {
    onPaddingChange?.(isPadded ? 'small' : 'none')
  }

  const handleResize = useCallback(
    (
      _event: MouseEvent | TouchEvent,
      _direction: ResizeDirection,
      _element: HTMLElement,
      delta: NumberSize,
    ) => {
      const currentHeight =
        size?.height === 'auto' || `${size?.height ?? 0}`.includes('%')
          ? size?.height
          : (asPx(size?.height ?? 0) as number)
      const currentWidth =
        size?.width === 'auto' || `${size?.width ?? 0}`.includes('%')
          ? size?.width
          : (asPx(size?.width ?? 0) as number)

      onSizeChange?.({
        height: isNumber(currentHeight)
          ? Math.max(currentHeight + delta.height, 0)
          : currentHeight,
        width: isNumber(currentWidth)
          ? Math.max(currentWidth + delta.width, 0)
          : currentWidth,
      })
    },
    [size?.height, size?.width, onSizeChange],
  )

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const imageElement = event.target as HTMLImageElement

      if (
        ((!isNumber(size?.width) && (asPx(size?.width) as number) === 0) ||
          (!isNumber(size?.height) && (asPx(size?.height) as number) === 0)) &&
        isFunction(onSizeChange)
      ) {
        onSizeChange({
          height: imageElement.naturalHeight,
          width: imageElement.naturalWidth,
        })
      }
    },
    [size?.width, size?.height, onSizeChange],
  )

  const [isZoomed, setIsZoomed] = useState(false)
  const handleZoomIn = () => {
    if (!editable && !isEmpty(imageUrl)) {
      setIsZoomed(true)
    }
  }
  const exitZoom = useCallback(() => setIsZoomed(false), [setIsZoomed])

  const hasUrl = !isEmpty(imageUrl)

  const trigger = (
    <div
      className={cj(
        'group relative flex size-full justify-center',
        hasUrl ? '' : 'self-center',
      )}
    >
      {withHint && hasUrl && (
        <div className="group-hover:hidden size-full bg-[rgba(0,0,0,0.3)] absolute top-0 left-0 flex items-center justify-center z-10 rounded-sm">
          <EditIcon className="text-white size-5" />
        </div>
      )}

      {hasUrl ? (
        <ImageDisplay
          exitZoom={exitZoom}
          isLoading={isImageLoading}
          maxWidth={maxWidth === '100%' ? undefined : maxWidth}
          objectFit={objectFit}
          setIsLoading={setIsImageLoading}
          size={size}
          url={imageUrl}
          withLoader={withLoader}
          zoomed={isZoomed}
          onClick={handleZoomIn}
          onLoad={handleImageLoad}
          {...omit(['className', 'url', 'setIsLoading'], imageProps)}
        />
      ) : (
        <AddImageButton
          isLoading={isImageLoading}
          label={buttonLabel}
          progress={progress}
          small={small}
        />
      )}

      {allowFitChange && !isEmpty(imageUrl) && !isImageLoading && editable && (
        <div className="absolute right-[calc(theme(spacing.3)+theme(spacing.half))] top-3 z-[48] flex select-none items-center rounded-sm bg-grey-90 p-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Switch
            checked={objectFit === 'cover'}
            labels={['Contain', 'Cover']}
            tabIndex={-1}
            onClick={(event) => event.stopPropagation()}
            onToggle={handleObjectFitChange}
          />
        </div>
      )}

      {allowPaddingChange &&
        !isEmpty(imageUrl) &&
        !isImageLoading &&
        editable && (
          <div className="absolute left-[calc(theme(spacing.3)+theme(spacing.half))] top-3 z-[48] flex select-none items-center rounded-sm bg-grey-90 p-1 opacity-0 transition-opacity group-hover:opacity-100">
            <Switch
              checked={padding === 'small'}
              labels={[
                $t('editor.blocks.image.padding.none'),
                $t('editor.blocks.image.padding.small'),
              ]}
              tabIndex={-1}
              onClick={(event) => event.stopPropagation()}
              onToggle={handlePaddingChange}
            />
          </div>
        )}
    </div>
  )

  const wrapperClassName = cj(
    'relative flex size-full justify-center p-0 pointer-events-auto max-h-[unset] min-h-8',
    editable ? 'cursor-pointer' : '',
    small ? '' : 'items-center',
    padding === 'small' ? 'p-1' : undefined,
  )

  const commonHandleClassNames =
    'rounded-sm bg-secondary border-1 border-white opacity-0 group-hover:opacity-100 transition-opacity z-[48]'
  const commonVerticalClassNames = 'h-6 max-h-1/2 top-1/2 -translate-y-1/2 w-1'
  const commonHorizontalClassNames =
    'w-6 max-w-1/2 left-1/2 -translate-x-1/2 h-1'
  const commonCornerClassNames = 'rounded-sm size-6 z-[48]'
  const handleClassNames = {
    bottom: cj(commonHandleClassNames, commonHorizontalClassNames, 'bottom-1'),
    bottomLeft: cj(commonCornerClassNames, 'bottom-1 left-1'),
    bottomRight: cj(commonCornerClassNames, 'bottom-1 right-1'),
    left: cj(commonHandleClassNames, commonVerticalClassNames, 'left-1'),
    right: cj(commonHandleClassNames, commonVerticalClassNames, 'right-1'),
    top: cj(commonHandleClassNames, commonHorizontalClassNames, 'top-1'),
    topLeft: cj(commonCornerClassNames, 'top-1 left-1'),
    topRight: cj(commonCornerClassNames, 'top-1 right-1'),
  }

  const commonHandleStyles = {
    bottom: undefined,
    height: undefined,
    left: undefined,
    right: undefined,
    top: undefined,
    width: undefined,
  }
  const handleStyles = {
    bottom: commonHandleStyles,
    bottomLeft: commonHandleStyles,
    bottomRight: commonHandleStyles,
    left: commonHandleStyles,
    right: commonHandleStyles,
    top: commonHandleStyles,
    topLeft: commonHandleStyles,
    topRight: commonHandleStyles,
  }

  let colorClassName = ''
  switch (actualColor) {
    case 'black': {
      colorClassName = 'bg-body-dark'
      break
    }

    case 'blue': {
      colorClassName = 'bg-decorative-blue-70'
      break
    }

    case 'green': {
      colorClassName = 'bg-decorative-green-70'
      break
    }

    case 'light-pink': {
      colorClassName = 'bg-decorative-pink-70'
      break
    }

    case 'pink': {
      colorClassName = 'bg-primary'
      break
    }

    case 'purple': {
      colorClassName = 'bg-decorative-purple-70'
      break
    }

    case 'transparent': {
      colorClassName = 'bg-transparent'
      break
    }

    case 'white': {
      colorClassName = 'bg-white'
      break
    }

    case 'yellow': {
      colorClassName = 'bg-decorative-yellow-70'
      break
    }

    default: {
      colorClassName = elevation === '0' ? 'bg-transparent' : 'bg-white'
      break
    }
  }

  const resizeEnabled =
    editable && resizable && !isEmpty(imageUrl) ? undefined : false

  const zoomable = !imageProps.asImg

  return (
    <Resizable
      className={cn(
        'group pointer-events-auto relative max-w-full',
        editable ? 'cursor-pointer' : zoomable ? 'cursor-zoom-in' : '',
        isEmpty(imageUrl) ||
          (isNumber(size?.height) ||
          (!size?.height?.includes('%') && size?.height !== 'auto')
            ? (asPx(size?.height) as number)
            : size?.height) !== 0
          ? ''
          : 'aspect-[1/1]',
        imageProps.className,
      )}
      defaultSize={{ height: 'auto', width: '100%' }}
      enable={resizeEnabled}
      handleClasses={handleClassNames}
      handleStyles={handleStyles}
      lockAspectRatio
      maxWidth={
        isNumber(asPx(maxWidth))
          ? (asPx(maxWidth) as number)
          : maxWidth || '100%'
      }
      minHeight={isEmpty(imageUrl) ? spacings[8] : spacings[20]}
      minWidth="15%"
      size={{ height: size?.height ?? 'auto', width: size?.width ?? '100%' }}
      onResizeStop={handleResize}
    >
      {(resizeEnabled === undefined || resizeEnabled) && (
        <div className="absolute z-[48] size-full opacity-0 transition-opacity group-hover:opacity-100">
          <div className="absolute -left-3 -top-3 -m-quarter -ml-half -mt-half">
            <div className="rotate-45">
              <ChevronLeft
                className="size-12 [&>path]:stroke-[0.25]"
                fill="black"
                stroke="white"
              />
            </div>
          </div>

          <div className="absolute -right-3 -top-3 -m-quarter -mr-half -mt-half">
            <div className="-rotate-45">
              <ChevronRight
                className="size-12 [&>path]:stroke-[0.25]"
                fill="black"
                stroke="white"
              />
            </div>
          </div>

          <div className="absolute -bottom-3 -right-3 -m-quarter -mb-1">
            <div className="rotate-45">
              <ChevronRight
                className="size-12 [&>path]:stroke-[0.25]"
                fill="black"
                stroke="white"
              />
            </div>
          </div>

          <div className="absolute -bottom-3 -left-3 -m-quarter -mb-1">
            <div className="-rotate-45">
              <ChevronLeft
                className="size-12 [&>path]:stroke-[0.25]"
                fill="black"
                stroke="white"
              />
            </div>
          </div>
        </div>
      )}

      {elevation === '0' ? (
        <div className={cj(wrapperClassName, colorClassName, 'rounded-sm')}>
          {trigger}
        </div>
      ) : (
        <Paper
          className={cj(wrapperClassName, colorClassName)}
          elevation={elevation}
        >
          {trigger}
        </Paper>
      )}
    </Resizable>
  )
}
